import React, { useEffect, useState, useCallback } from "react";
import { api } from "../lib/api";
import { Button } from "./ui/button";
import { Badge } from "./ui/badge";
import { Input } from "./ui/input";
import {
    Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "./ui/dialog";
import { toast } from "sonner";
import { RefreshCw, Send, Mail, CheckCircle2, XCircle, AlertCircle, Clock } from "lucide-react";

const TRIGGER_LABELS = {
    new_goal: "New goal",
    new_task: "New task",
    progress_update: "Progress update",
    task_completed: "Task completed",
    manual_test: "Manual test",
};

const STATUS_META = {
    sent: { label: "Delivered", className: "bg-[#7A9E72]/15 text-[#4A6A47]", Icon: CheckCircle2 },
    failed: { label: "Failed", className: "bg-[#B23A48]/15 text-[#B23A48]", Icon: XCircle },
    skipped: { label: "Skipped", className: "bg-[#D97B48]/15 text-[#9C5429]", Icon: AlertCircle },
    pending: { label: "Pending", className: "bg-[#57675F]/10 text-[#57675F]", Icon: Clock },
};

const fmtWhen = (iso) => {
    if (!iso) return "—";
    try {
        const d = new Date(iso);
        return d.toLocaleString(undefined, { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" });
    } catch { return iso; }
};

export function EmailActivityDialog({ open, onClose, adminEmail }) {
    const [logs, setLogs] = useState([]);
    const [health, setHealth] = useState(null);
    const [loading, setLoading] = useState(false);
    const [sending, setSending] = useState(false);
    const [testTo, setTestTo] = useState(adminEmail || "");

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const [h, l] = await Promise.all([
                api.get("/email/health"),
                api.get("/email/logs?limit=50"),
            ]);
            setHealth(h.data);
            setLogs(l.data || []);
        } catch (e) {
            toast.error("Could not load email activity");
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        if (open) load();
    }, [open, load]);

    useEffect(() => {
        if (adminEmail && !testTo) setTestTo(adminEmail);
    }, [adminEmail, testTo]);

    const sendTest = async () => {
        if (!testTo.trim()) { toast.error("Enter a recipient"); return; }
        setSending(true);
        try {
            const { data } = await api.post("/email/test", { to: testTo.trim() });
            if (data.ok) toast.success(`Test sent to ${testTo.trim()}`);
            else toast.error("Send failed — check log row below for reason");
            await load();
        } catch (e) {
            toast.error("Send failed");
        } finally {
            setSending(false);
        }
    };

    return (
        <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
            <DialogContent className="max-w-3xl bg-[#F9F8F3] border-[#E3E5DD]" data-testid="email-activity-dialog">
                <DialogHeader>
                    <DialogTitle className="font-display text-2xl text-[#1E2623] flex items-center gap-2">
                        <Mail className="w-5 h-5 text-[#0F5C60]" /> Email Activity
                    </DialogTitle>
                    <DialogDescription className="text-[#57675F]">
                        Every notification this app tries to send is logged here — even if Resend is misconfigured.
                    </DialogDescription>
                </DialogHeader>

                {/* Health panel */}
                <div className="rounded-2xl border border-[#E3E5DD] bg-white p-4" data-testid="email-health-panel">
                    {!health ? (
                        <div className="text-sm text-[#57675F]">Loading config…</div>
                    ) : (
                        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-sm">
                            <div>
                                <div className="text-[10px] uppercase tracking-wider text-[#57675F]">Resend</div>
                                <div className="mt-0.5 font-medium text-[#1E2623]" data-testid="health-resend-configured">
                                    {health.resend_configured ? (
                                        <span className="text-[#4A6A47]">Configured</span>
                                    ) : (
                                        <span className="text-[#B23A48]">Missing key</span>
                                    )}
                                </div>
                                {health.key_prefix && <div className="text-xs text-[#57675F] mt-0.5">{health.key_prefix}</div>}
                            </div>
                            <div>
                                <div className="text-[10px] uppercase tracking-wider text-[#57675F]">From</div>
                                <div className="mt-0.5 text-[#1E2623] truncate" title={health.sender_email}>{health.sender_email}</div>
                            </div>
                            <div>
                                <div className="text-[10px] uppercase tracking-wider text-[#57675F]">Sent / Failed</div>
                                <div className="mt-0.5 text-[#1E2623]">
                                    <span className="text-[#4A6A47] font-medium">{health.counts?.sent ?? 0}</span>
                                    <span className="text-[#57675F] mx-1">·</span>
                                    <span className="text-[#B23A48] font-medium">{health.counts?.failed ?? 0}</span>
                                </div>
                            </div>
                            <div>
                                <div className="text-[10px] uppercase tracking-wider text-[#57675F]">Total logged</div>
                                <div className="mt-0.5 text-[#1E2623] font-medium">{health.counts?.total ?? 0}</div>
                            </div>
                        </div>
                    )}
                </div>

                {/* Test send */}
                <div className="rounded-2xl border border-[#E3E5DD] bg-white p-4 flex flex-col sm:flex-row gap-2 sm:items-end" data-testid="email-test-panel">
                    <div className="flex-1">
                        <label className="text-[10px] uppercase tracking-wider text-[#57675F]">Send test email to</label>
                        <Input
                            value={testTo}
                            onChange={(e) => setTestTo(e.target.value)}
                            placeholder="you@example.com"
                            className="mt-1 bg-white border-[#E3E5DD]"
                            data-testid="email-test-input"
                        />
                    </div>
                    <Button
                        onClick={sendTest}
                        disabled={sending}
                        className="bg-[#0F5C60] hover:bg-[#0A464A] text-white rounded-full"
                        data-testid="email-test-send-button"
                    >
                        <Send className="w-4 h-4 mr-2" /> {sending ? "Sending…" : "Send test"}
                    </Button>
                    <Button
                        variant="outline"
                        onClick={load}
                        disabled={loading}
                        className="rounded-full border-[#E3E5DD]"
                        data-testid="email-refresh-button"
                    >
                        <RefreshCw className={`w-4 h-4 mr-2 ${loading ? "animate-spin" : ""}`} /> Refresh
                    </Button>
                </div>

                {/* Log list */}
                <div className="max-h-[55vh] overflow-y-auto rounded-2xl border border-[#E3E5DD] bg-white divide-y divide-[#E3E5DD]" data-testid="email-log-list">
                    {logs.length === 0 ? (
                        <div className="p-6 text-center text-sm text-[#57675F]">
                            {loading ? "Loading…" : "No emails attempted yet. Create a task or send a test to begin."}
                        </div>
                    ) : (
                        logs.map((log) => {
                            const meta = STATUS_META[log.status] || STATUS_META.pending;
                            const Icon = meta.Icon;
                            return (
                                <div key={log.log_id} className="px-4 py-3 flex flex-col sm:flex-row sm:items-center gap-2" data-testid={`email-log-row-${log.log_id}`}>
                                    <div className="flex-1 min-w-0">
                                        <div className="flex items-center gap-2 flex-wrap">
                                            <Badge className={`${meta.className} rounded-full px-2 py-0.5 text-[10px] uppercase tracking-wider`}>
                                                <Icon className="w-3 h-3 mr-1 inline" />{meta.label}
                                            </Badge>
                                            <span className="text-[10px] uppercase tracking-wider text-[#57675F]">{TRIGGER_LABELS[log.trigger] || log.trigger}</span>
                                        </div>
                                        <div className="text-sm font-medium text-[#1E2623] mt-1 truncate" title={log.subject}>{log.subject}</div>
                                        <div className="text-xs text-[#57675F] mt-0.5 truncate">
                                            to {Array.isArray(log.to) ? log.to.join(", ") : log.to || "—"}
                                        </div>
                                        {log.error && (
                                            <div className="text-xs text-[#B23A48] mt-1 break-words">⚠ {log.error}</div>
                                        )}
                                    </div>
                                    <div className="text-xs text-[#57675F] sm:text-right whitespace-nowrap">
                                        {fmtWhen(log.created_at)}
                                        {log.resend_id && <div className="text-[10px] text-[#57675F]/70 mt-0.5 truncate max-w-[180px]" title={log.resend_id}>id: {log.resend_id.slice(0, 12)}…</div>}
                                    </div>
                                </div>
                            );
                        })
                    )}
                </div>
            </DialogContent>
        </Dialog>
    );
}
